import React, { useState } from 'react';
import { Building2, User as UserIcon, Home, Briefcase } from 'lucide-react';

/**
 * Onda 8 abr/26 — EntityLink universal.
 * Substitui todas as navegações <Link to="/" state={{ focusEmpresaId }}> por click
 * que abre o modal correspondente (QuickView ou Detail) in-place.
 *
 * Regra: NUNCA navegar para o Dashboard só para ver uma empresa.
 *
 * Lazy import dos modais evita ciclos (EmpresaQuickView → EntityLink → ImovelDetail, etc.).
 *
 * Props:
 *   - entityType: 'empresa' | 'contato' | 'imovel' | 'oferta'
 *   - entityId: uuid
 *   - label: texto exibido (default '—')
 *   - className
 *   - onClick extra (opcional, recebe o evento ANTES de abrir o modal)
 */

const ICONS = { empresa: Building2, contato: UserIcon, imovel: Home, oferta: Briefcase };

const ID_PROP = {
  empresa: 'empresaId',
  contato: 'contatoId',
  imovel: 'imovelId',
  oferta: 'ofertaId',
};

async function loadModal(entityType) {
  switch (entityType) {
    case 'empresa': return (await import('../EmpresaQuickViewModal')).default;
    case 'contato': return (await import('../ContatoQuickViewModal')).default;
    case 'imovel':  return (await import('../imoveis/ImovelDetailModal')).default;
    case 'oferta':  return (await import('../OfertaDetailModal')).default;
    default: return null;
  }
}

export default function EntityLink({ entityType, entityId, label, className = '', showIcon = true }) {
  const [Modal, setModal] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!entityId || !entityType) return <span className="text-slate-400">—</span>;
  const Icon = ICONS[entityType];

  const handleClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (Modal || loading) return;
    setLoading(true);
    try {
      const M = await loadModal(entityType);
      if (M) setModal(() => M);
    } finally {
      setLoading(false);
    }
  };

  const idProp = ID_PROP[entityType];

  return (
    <>
      <button
        onClick={handleClick}
        disabled={loading}
        className={`text-blue-700 hover:underline truncate inline-flex items-center gap-1 text-left max-w-full ${className}`}
        title={label}
      >
        {showIcon && Icon && <Icon size={11} className="flex-shrink-0" />}
        <span className="truncate">{label || '—'}</span>
      </button>
      {Modal && (
        <Modal {...{ [idProp]: entityId }} onClose={() => setModal(null)} />
      )}
    </>
  );
}
